import { useEffect, useRef, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { downloadPublicMedia } from '@/lib/downloads/clip-download';
import {
  isMediaDownloadPending,
  type TopEarningClipRow,
} from '@/lib/campaigns/topEarningClips';
import {
  ADMIN_CPM_SUBMISSION_MEDIA_DOWNLOAD,
  ADMIN_ENQUEUE_CPM_SUBMISSION_MEDIA_DOWNLOAD,
} from '@/queries/queries';

const POLL_MS = 2500;
const POLL_LIMIT = 72;

export type SubmissionMediaDownloadState = {
  status: string;
  error?: string | null;
};

type MediaDownloadPayload = {
  id?: string | null;
  submission_id: string;
  status: string;
  provider?: string | null;
  video_url?: string | null;
  error?: string | null;
};

export function useCpmSubmissionMediaDownload() {
  const [enqueue] = useMutation(ADMIN_ENQUEUE_CPM_SUBMISSION_MEDIA_DOWNLOAD);
  const [fetchStatus] = useLazyQuery(ADMIN_CPM_SUBMISSION_MEDIA_DOWNLOAD, {
    fetchPolicy: 'network-only',
  });
  const [rowState, setRowState] = useState<Record<string, SubmissionMediaDownloadState>>({});
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const attempts = useRef<Record<string, number>>({});

  useEffect(() => () => {
    Object.values(timers.current).forEach((timer) => clearTimeout(timer));
  }, []);

  const setStatus = (clipId: string, next: SubmissionMediaDownloadState) => {
    setRowState((current) => ({ ...current, [clipId]: next }));
  };

  const clearTimer = (clipId: string) => {
    if (timers.current[clipId]) {
      clearTimeout(timers.current[clipId]);
      delete timers.current[clipId];
    }
  };

  const saveFile = async (clip: TopEarningClipRow, videoUrl: string) => {
    await downloadPublicMedia(videoUrl, `${clip.platform}-${clip.id}`);
  };

  const handlePayload = async (clip: TopEarningClipRow, payload?: MediaDownloadPayload | null) => {
    if (!payload) {
      setStatus(clip.id, { status: 'failed', error: 'missing_payload' });
      return;
    }
    setStatus(clip.id, { status: payload.status, error: payload.error });
    if (payload.status === 'success' && payload.video_url) {
      clearTimer(clip.id);
      await saveFile(clip, payload.video_url);
      return;
    }
    if (payload.status === 'failed' || payload.status === 'unsupported') {
      clearTimer(clip.id);
      return;
    }
    if (isMediaDownloadPending(payload.status)) {
      schedulePoll(clip);
    }
  };

  const schedulePoll = (clip: TopEarningClipRow) => {
    clearTimer(clip.id);
    const nextAttempt = (attempts.current[clip.id] || 0) + 1;
    if (nextAttempt > POLL_LIMIT) {
      setStatus(clip.id, { status: 'failed', error: 'timeout' });
      return;
    }
    attempts.current[clip.id] = nextAttempt;
    timers.current[clip.id] = setTimeout(async () => {
      try {
        const { data } = await fetchStatus({ variables: { submissionId: clip.id } });
        await handlePayload(clip, data?.adminCpmSubmissionMediaDownload);
      } catch (error: unknown) {
        setStatus(clip.id, {
          status: 'failed',
          error: error instanceof Error ? error.message : 'poll_failed',
        });
      }
    }, POLL_MS);
  };

  const downloadClip = async (clip: TopEarningClipRow) => {
    if (isMediaDownloadPending(rowState[clip.id]?.status)) return;
    attempts.current[clip.id] = 0;
    setStatus(clip.id, { status: 'queued' });
    try {
      const { data } = await enqueue({ variables: { submissionId: clip.id } });
      await handlePayload(clip, data?.adminEnqueueCpmSubmissionMediaDownload);
    } catch (error: unknown) {
      setStatus(clip.id, {
        status: 'failed',
        error: error instanceof Error ? error.message : 'enqueue_failed',
      });
    }
  };

  return { downloadClip, rowState };
}
